import { AddRounded, DeleteOutlineRounded, EditRounded, Search, VisibilityRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  CardActions,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from "@mui/material";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import Loading from "../../../../components/loading/loading";
import useUser from "../../../../providers/user/user_provider";
import OrgSelector from "../../../../components/org_selector/org_selector";
import { ToolbarButton } from "../../../../components/toolbar_button/toolbar_button";
import FilterIcon from "../../../../assets/filter_icon.svg";
import SortIcon from "../../../../assets/sort.svg";
import { cms_add_banner, cms_edit_banner, cms_view_banner } from "../../../../routes/routes_path";
import { Banner, bannerTypeLabel } from "../../models/cms";
import { black, green, greenLight, purple, purpleLight, red, redLight } from "../../../../theme/colors";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../../../users/components/table_toolkit/table_toolkit";

export type StatusFilter = "all" | "draft" | "published";

const ROWS_PER_PAGE = 10;

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset": { borderColor: "transparent" },
    "&:hover fieldset": { borderColor: "transparent" },
    "& fieldset": { borderColor: "transparent" },
  },
}));

interface BannerListingProps {
  list: Banner[];
  count: number;
  page: number;
  setPage: (page: number) => void;
  isLoading: boolean;
  searchValue: string;
  setSearchValue: (val: string) => void;
  filterCount: number;
  onOpenFilters: () => void;
  sortLabel: string;
  onOpenSort: () => void;
  onDelete: (item: Banner) => void;
}

export default function BannerListing({
  list,
  count,
  page,
  setPage,
  isLoading,
  searchValue,
  setSearchValue,
  filterCount,
  onOpenFilters,
  sortLabel,
  onOpenSort,
  onDelete,
}: BannerListingProps) {
  const navigate = useNavigate();
  const { hasPermission } = useUser();

  return (
    <Box display={"flex"} flexDirection={"column"} sx={{ height: "100%" }}>
      <Box
        sx={{ p: 2 }}
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <TextField
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          size="small"
          fullWidth
          sx={{ maxWidth: 420, backgroundColor: "#F5E6F5", borderRadius: 3, borderColor: "transparent" }}
          placeholder="Search Banners"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "text.primary" }} />
              </InputAdornment>
            ),
          }}
        />

        <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5}>
          <OrgSelector lockToOwnOrg />
          <ToolbarButton icon={SortIcon} label={sortLabel} onClick={onOpenSort} />
          <ToolbarButton icon={FilterIcon} label="Filters" badgeCount={filterCount} onClick={onOpenFilters} />

          {hasPermission("cms", "create") && (
            <Button
              variant="contained"
              disableElevation
              startIcon={<AddRounded />}
              onClick={() => navigate(cms_add_banner)}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Add Banner
            </Button>
          )}
        </Box>
      </Box>
      <CardContent sx={{ display: "flex", flexDirection: "column", flexGrow: 1, flex: 1 }}>
        {isLoading ? (
          <Box display="flex" justifyContent={"center"} alignItems={"center"} py={8}>
            <Loading />
          </Box>
        ) : list?.length === 0 ? (
          <TableEmptyState message="No banners found" hint="Try adjusting your search or filters." />
        ) : (
          <Box mt={1}>
            <TableHeaderRow
              labels={[
                { label: "Sl. No.", flex: 0.2 },
                { label: "Banner Id", flex: 0.35 },
                { label: "Banner", flex: 1.95 },
                { label: "Actions" },
              ]}
              lastColumnWidth={170}
              align="left"
              bordered
            />
            {list.map((item, index) => (
              <TableRow key={item.id} bordered>
                <TableCell label="Sl. No." flex={0.2} bordered>
                  <Typography fontSize={14} color={black} noWrap>
                    {(page - 1) * ROWS_PER_PAGE + index + 1}
                  </Typography>
                </TableCell>
                <TableCell label="Banner Id" flex={0.35} bordered>
                  <Typography fontSize={14} color={black} noWrap>
                    {item?.id}
                  </Typography>
                </TableCell>
                <TableCell label="Banner" flex={1.95} bordered>
                  <Box display="flex" flexDirection="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography fontWeight={600} color={black} noWrap>
                      {item?.title}
                    </Typography>
                    <Chip
                      size="small"
                      label={item?.status === "published" ? "Published" : "Draft"}
                      color={item?.status === "published" ? "success" : "default"}
                    />
                    <Chip size="small" variant="outlined" label={bannerTypeLabel(item?.banner_type)} />
                  </Box>
                </TableCell>
                <TableCell isLast lastColumnWidth={170} bordered>
                  <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }} gap={0.5}>
                    <Tooltip title="View details">
                      <IconButton
                        onClick={() => navigate(`${cms_view_banner}/${item.id}`)}
                        sx={{ bgcolor: greenLight, "&:hover": { bgcolor: "#D6EEE0" } }}
                      >
                        <VisibilityRounded sx={{ color: green, fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                    {hasPermission("cms", "update") && (
                      <Tooltip title="Edit banner">
                        <IconButton
                          onClick={() => navigate(`${cms_edit_banner}/${item.id}`)}
                          sx={{ bgcolor: purpleLight, "&:hover": { bgcolor: "#F0D9F0" } }}
                        >
                          <EditRounded sx={{ color: purple, fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasPermission("cms", "delete") && (
                      <Tooltip title="Delete banner">
                        <IconButton
                          onClick={() => onDelete(item)}
                          sx={{ bgcolor: redLight, "&:hover": { bgcolor: "#FBD9D6" } }}
                        >
                          <DeleteOutlineRounded sx={{ color: red, fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </Box>
        )}
      </CardContent>
      {list?.length > 0 && (
        <CardActions sx={{ px: 3, pb: 3 }}>
          <Box display="flex" width="100%">
            <TableFooterPagination
              count={count}
              page={page - 1}
              onPageChange={(_, newPage) => setPage(newPage + 1)}
              summaryText={`Showing ${count ? (page - 1) * ROWS_PER_PAGE + 1 : 0} to ${Math.min(page * ROWS_PER_PAGE, count)} of ${count} entries`}
              rowsPerPage={ROWS_PER_PAGE}
            />
          </Box>
        </CardActions>
      )}
    </Box>
  );
}
